// The "show your work" donations read (issue #116): the individual donations behind a processor's
// pinned fee estimate for the owed period. Lazily fetched — `enabled` is driven by the expand
// popover's open state, so an unexpanded row costs nothing. The server resolves the SAME window the
// pin was computed over (it takes NO period), so the listed donations reconcile with the shown
// total. This answers "where did this number come from?", never "which donations is this increment
// for?" — the fee has no per-donation attribution.

import { useQuery } from '@tanstack/react-query';
import type { ProcessorFeeDonationsResponse } from '@signalvote/shared';
import { apiJson } from './apiFetch';
import { queryKeys } from '../../lib/queryKeys';
import { FINANCE_STALE_TIME } from './queryUtils';

/**
 * Fetch the donations feeding a processor's fee estimate for the owed period. Enabled only when
 * `enabled` is true (the popover is open), so the list is fetched on demand, not for every pinned
 * row. `authenticatedFetch` adds the X-Subsidiary + X-Campaign-Id headers; the key is cid()-scoped.
 */
export function useProcessorFeeDonations(processorId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.finance.processorFeeDonations(processorId),
    queryFn: () =>
      apiJson<ProcessorFeeDonationsResponse>(`/api/finance/processor-fees/${processorId}/donations`, {
        error: 'Failed to load the donations behind this fee',
      }),
    enabled,
    staleTime: FINANCE_STALE_TIME,
  });
}
